import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  BrowserRouter,
  HashRouter,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { useToast } from "@/hooks/use-toast";
import { useEffect, lazy, Suspense } from "react";
import { MobileLayout } from "@/components/mobile/MobileLayout";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AppProvider } from "@/components/AppProvider";
import { StripeProvider } from "@/components/StripeProvider";
import { PublicApp } from "@/components/PublicApp";
import { EmailConfirmationBanner } from "@/components/EmailConfirmationBanner";


import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Onboarding from "./pages/Onboarding";
import NewUserOnboarding from "./pages/NewUserOnboarding";
import MobileTasks from "./pages/MobileTasks";
import CreateTask from "./pages/CreateTask";
import EditTask from "./pages/EditTask";
import EditPerson from "./pages/EditPerson";
import EditEvent from "./pages/EditEvent";
import MobileCalendar from "./pages/MobileCalendar";
import CreateEvent from "./pages/CreateEvent";
import MobileContacts from "./pages/MobileContacts";
import CreateContact from "./pages/CreateContact";
import Persons from "./pages/Persons";
import CreatePerson from "./pages/CreatePerson";
import MobileDocuments from "./pages/MobileDocuments";
import Index from "./pages/Index";
import EventDetails from "./pages/EventDetails";
import PublicEventAdmin from "./pages/PublicEventAdmin";
import InvitationLanding from "./pages/InvitationLanding";
import { Upgrade } from "./pages/Upgrade";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import SubscriptionCancelled from "./pages/SubscriptionCancelled";
import Loading from "./pages/Loading";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import MentionsLegales from "./pages/MentionsLegales";
import Support from "./pages/Support";
import InviteRedirect from "./pages/InviteRedirect";
import { CreateTaskListForm } from "./components/tasks/CreateTaskListForm";
import { TaskListDetailView } from "./components/tasks/TaskListDetailView";
import { TaskListSettings } from "./components/tasks/TaskListSettings";

import { FluidNavigationProvider } from "@/contexts/FluidNavigationContext";
import { PaymentModalProvider } from "@/contexts/PaymentModalContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { initPush } from "@/push/initPush";
import { useAuth } from "@/hooks/useAuth";
import React from "react";

// ✅ lazy pour que le host ne bloque jamais l'affichage si une modale a un souci
const ModalHost = lazy(() =>
  import("@/components/mobile/ModalHost").then((m) => ({ default: m.ModalHost }))
);

// Petit filet anti-crash local
class Boundary extends React.Component<{ children: React.ReactNode }, { error?: any }> {
  state = { error: undefined as any };
  static getDerivedStateFromError(error: any) { return { error }; }
  componentDidCatch(error: any, info: any) { console.error("[ModalHost boundary]", error, info); }
  render() { return this.state.error ? null : this.props.children; }
}

const Router = Capacitor.isNativePlatform() ? HashRouter : BrowserRouter;

const MobileLayoutWrapper = ({ children }: { children: React.ReactNode }) => (
  <MobileLayout>{children}</MobileLayout>
);

const AppContent = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const location = useLocation();
  const { user } = useAuth();

  // Global navigation event listener for SPA navigation from hooks
  useEffect(() => {
    const handleAppNavigate = (event: CustomEvent<{ path: string; replace?: boolean }>) => {
      const { path, replace } = event.detail;
      console.log('🧭 app:navigate event received:', path, { replace });
      navigate(path, { replace: replace ?? false });
    };

    window.addEventListener('app:navigate', handleAppNavigate as EventListener);
    return () => {
      window.removeEventListener('app:navigate', handleAppNavigate as EventListener);
    };
  }, [navigate]);

  useEffect(() => {
    const hideSplash = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          const { SplashScreen } = await import("@capacitor/splash-screen");
          await SplashScreen.hide();
        } catch (e) {
          console.error("Splash hide failed:", e);
        }
      }
    };
    hideSplash();
  }, []);

  // Initialize push notifications
  useEffect(() => {
    if (Capacitor.isNativePlatform() && user?.id) {
      initPush(user.id).catch(err => {
        console.error('Failed to initialize push notifications:', err);
      });
    }
  }, [user?.id]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let removeListener: (() => void) | undefined;

    const setupNativeListeners = async () => {
      try {
        const [{ App: CapacitorApp }, { Browser }] = await Promise.all([
          import("@capacitor/app"),
          import("@capacitor/browser"),
        ]);

        const listener = await CapacitorApp.addListener(
          "appUrlOpen",
          async (event) => {
            const url = event.url;
            console.log("Deep link opened:", url);

            // Ignore clan:// URLs - handled by main.tsx
            if (url.includes("clan://")) {
              console.log("Ignoring clan:// URL in App.tsx (handled in main.tsx)");
              return;
            }

            if (url.includes("kin://oauth-callback")) {
              const urlObj = new URL(url);
              const accessToken = urlObj.searchParams.get("access_token");
              const refreshToken = urlObj.searchParams.get("refresh_token");
              const expiresIn = urlObj.searchParams.get("expires_in");
              const tokenType = urlObj.searchParams.get("token_type");
              const error = urlObj.searchParams.get("error");

              const { Browser } = await import("@capacitor/browser");
              await Browser.close();

              if (error) {
                let errorMessage = "Une erreur s'est produite lors de la connexion.";
                switch (error) {
                  case "access_denied":
                    errorMessage = "Vous avez annulé la connexion à Google Calendar.";
                    break;
                  case "invalid_request":
                    errorMessage = "Demande invalide. Veuillez réessayer.";
                    break;
                  case "server_error":
                    errorMessage = "Erreur serveur. Veuillez réessayer plus tard.";
                    break;
                }
                toast({
                  title: "Erreur de connexion",
                  description: errorMessage,
                  variant: "destructive",
                });
              } else if (accessToken) {
                const tokens = {
                  access_token: accessToken,
                  refresh_token: refreshToken,
                  expires_at: Date.now() + (parseInt(expiresIn || "3600") * 1000),
                  token_type: tokenType || "Bearer",
                };
                localStorage.setItem("google_calendar_tokens", JSON.stringify(tokens));
                toast({
                  title: "Connexion réussie",
                  description: "Votre compte Google Calendar est maintenant connecté !",
                });
                navigate("/dashboard");
              }
            }
          }
        );

        removeListener = () => listener.remove();
      } catch (error) {
        console.error("Failed to load Capacitor plugins:", error);
      }
    };

    setupNativeListeners();
    return () => {
      if (removeListener) removeListener();
    };
  }, [navigate, toast]);

  return (
    <Routes>
      {/* Pages publiques sans navigation - redirection auto si connecté */}
      <Route path="/" element={<ProtectedRoute requireAuth={false}><Index /></ProtectedRoute>} />
      <Route path="/auth" element={<ProtectedRoute requireAuth={false}><Auth /></ProtectedRoute>} />
      <Route path="/loading" element={<Loading />} />
      <Route path="/landing" element={<ProtectedRoute requireAuth={false}><Index /></ProtectedRoute>} />
      <Route path="/invite/:token" element={<InviteRedirect />} />
      <Route path="/invitation/:token" element={<InvitationLanding />} />
      <Route path="/public-event-admin/:slug" element={<PublicEventAdmin />} />
      <Route path="/upgrade" element={<Upgrade />} />
      <Route path="/subscription-success" element={<SubscriptionSuccess />} />
      <Route path="/subscription-cancelled" element={<SubscriptionCancelled />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/mentions-legales" element={<MentionsLegales />} />
      <Route path="/support" element={<Support />} />
      
      {/* Pages authentifiées avec navigation - redirection auto si non connecté */}
      <Route path="/dashboard" element={<ProtectedRoute><MobileLayoutWrapper><Dashboard /></MobileLayoutWrapper></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><MobileLayoutWrapper><Onboarding /></MobileLayoutWrapper></ProtectedRoute>} />
      <Route path="/new-user-onboarding" element={<ProtectedRoute><NewUserOnboarding /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><MobileLayoutWrapper><MobileTasks /></MobileLayoutWrapper></ProtectedRoute>} />
      <Route path="/create-task-list" element={<ProtectedRoute><MobileLayoutWrapper><CreateTaskListForm /></MobileLayoutWrapper></ProtectedRoute>} />
      <Route path="/task-list/:listId" element={<ProtectedRoute><MobileLayoutWrapper><TaskListDetailView /></MobileLayoutWrapper></ProtectedRoute>} />
      <Route path="/task-list-settings/:listId" element={<ProtectedRoute><MobileLayoutWrapper><TaskListSettings /></MobileLayoutWrapper></ProtectedRoute>} />
      <Route path="/create-task" element={<ProtectedRoute><MobileLayoutWrapper><CreateTask /></MobileLayoutWrapper></ProtectedRoute>} />
      <Route path="/tasks/create" element={<ProtectedRoute><MobileLayoutWrapper><CreateTask /></MobileLayoutWrapper></ProtectedRoute>} />
      <Route path="/tasks/edit/:id" element={<ProtectedRoute><MobileLayoutWrapper><EditTask /></MobileLayoutWrapper></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><MobileLayoutWrapper><MobileCalendar /></MobileLayoutWrapper></ProtectedRoute>} />
      <Route path="/events/create" element={<ProtectedRoute><MobileLayoutWrapper><CreateEvent /></MobileLayoutWrapper></ProtectedRoute>} />
      <Route path="/events/edit/:id" element={<ProtectedRoute><MobileLayoutWrapper><EditEvent /></MobileLayoutWrapper></ProtectedRoute>} />
      <Route path="/event/:id" element={<ProtectedRoute><MobileLayoutWrapper><EventDetails /></MobileLayoutWrapper></ProtectedRoute>} />
      <Route path="/contacts" element={<ProtectedRoute><MobileLayoutWrapper><MobileContacts /></MobileLayoutWrapper></ProtectedRoute>} />
      <Route path="/contacts/create" element={<ProtectedRoute><MobileLayoutWrapper><CreateContact /></MobileLayoutWrapper></ProtectedRoute>} />
      <Route path="/persons" element={<ProtectedRoute><MobileLayoutWrapper><Persons /></MobileLayoutWrapper></ProtectedRoute>} />
      <Route path="/persons/create" element={<ProtectedRoute><MobileLayoutWrapper><CreatePerson /></MobileLayoutWrapper></ProtectedRoute>} />
      <Route path="/persons/edit/:id" element={<ProtectedRoute><MobileLayoutWrapper><EditPerson /></MobileLayoutWrapper></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><MobileLayoutWrapper><MobileDocuments /></MobileLayoutWrapper></ProtectedRoute>} />
      
      {/* Route par défaut - redirection vers auth si non connecté */}
      <Route path="*" element={<ProtectedRoute requireAuth={false}><Auth /></ProtectedRoute>} />
    </Routes>
  );
};

const App = () => {
  const isPublic = window.location.pathname.startsWith("/public-event/");

  if (isPublic) {
    return (
      <AppProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <PublicApp />
          </BrowserRouter>
        </TooltipProvider>
      </AppProvider>
    );
  }

  return (
    <AppProvider>
      <StripeProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <Router>
            <ScrollToTop />
            <EmailConfirmationBanner />
            
            <PaymentModalProvider>
              <FluidNavigationProvider>
                {/* tes pages */}
                <AppContent />
                {/* tes modales globales (contexte dispo) */}
                <Boundary>
                  <Suspense fallback={null}>
                    <ModalHost />
                  </Suspense>
                </Boundary>
              </FluidNavigationProvider>
            </PaymentModalProvider>
          </Router>
        </TooltipProvider>
      </StripeProvider>
    </AppProvider>
  );
};

export default App;
